/* eslint-disable no-unused-vars */
import React, { ElementType, ReactNode } from 'react';
import { Box, BoxProps, Collapse, Flex, FlexProps, Icon, Text, Tooltip } from '@chakra-ui/react';
import { CaretRight } from 'phosphor-react';
import { useSidebar } from './sidebar';

export const SidebarMenu = ({ children, ...rest }: BoxProps) => (
  <Box
    as="ul"
    listStyleType="none"
    display="flex"
    flexDirection="column"
    w="full"
    minW={0}
    {...rest}
  >
    {children}
  </Box>
);

export const SidebarMenuItem = ({ children, ...rest }: BoxProps) => (
  <Box as="li" position="relative" {...rest}>
    {children}
  </Box>
);

interface SidebarMenuButtonProps extends Omit<FlexProps, 'children'> {
  icon?: ElementType;
  isActive?: boolean;
  tooltip?: string;
  size?: 'sm' | 'default';
  children: ReactNode;
  endElement?: ReactNode;
}

export const SidebarMenuButton = ({
  icon,
  isActive = false,
  tooltip,
  size = 'default',
  children,
  endElement,
  ...rest
}: SidebarMenuButtonProps) => {
  const { state, isMobile } = useSidebar();
  const collapsed = state === 'collapsed' && !isMobile;

  const button = (
    <Flex
      as="button"
      type="button"
      w={collapsed ? '32px' : 'full'}
      h={size === 'sm' ? '28px' : '30px'}
      minH={collapsed ? '32px' : undefined}
      align="center"
      justify={collapsed ? 'center' : 'flex-start'}
      gap="8px"
      px={collapsed ? 0 : '8px'}
      mb="2px"
      mx={collapsed ? 'auto' : 0}
      borderRadius="9px"
      cursor="pointer"
      textAlign="left"
      overflow="hidden"
      sx={{ transition: 'background-color 120ms ease, color 120ms ease' }}
      bg={isActive ? 'surface.sidebarSelected' : 'transparent'}
      color={isActive ? 'content.strong' : 'content.body'}
      fontWeight={isActive ? 500 : 400}
      fontSize={size === 'sm' ? '12px' : '13px'}
      _hover={{ bg: 'surface.sidebarSelected', color: 'content.strong' }}
      data-active={isActive || undefined}
      {...rest}
    >
      {icon && <Icon as={icon} w="14px" h="14px" flexShrink={0} />}
      {!collapsed && (
        <Text as="span" noOfLines={1} flex="1" minW={0}>
          {children}
        </Text>
      )}
      {!collapsed && endElement}
    </Flex>
  );

  if (!tooltip) return button;

  return (
    <Tooltip label={tooltip} placement="right" isDisabled={!collapsed} openDelay={200}>
      {button}
    </Tooltip>
  );
};

interface SidebarMenuSubProps extends BoxProps {
  isOpen: boolean;
}

export const SidebarMenuSub = ({ isOpen, children, ...rest }: SidebarMenuSubProps) => {
  const { state, isMobile } = useSidebar();
  if (state === 'collapsed' && !isMobile) return null;

  return (
    <Collapse in={isOpen} animateOpacity>
      <Box
        as="ul"
        listStyleType="none"
        pl="14px"
        ml="12px"
        borderLeft="1px solid"
        borderColor="line.hairline"
        {...rest}
      >
        {children}
      </Box>
    </Collapse>
  );
};

export const SidebarMenuChevron = ({ isOpen }: { isOpen: boolean }) => (
  <Icon
    as={CaretRight}
    w="12px"
    h="12px"
    flexShrink={0}
    color="content.faint"
    transform={isOpen ? 'rotate(90deg)' : 'rotate(0deg)'}
    sx={{ transition: 'transform 180ms ease' }}
  />
);
